import React from 'react';
import PropTypes from 'prop-types';
import { Box, Checkbox, Typography } from '@mui/material';
import { sa } from '../../superAdminUi';
import { badgeSx, hintSx, rowSx, tone } from './panelStyles';

// The roles as a checklist with each role's permissions shown inline. A role that can't be used
// right now stays visible with the reason, rather than disappearing; a row that differs from what
// the user holds today is tinted and badged so the change is obvious before it is reviewed.
// Read-only, it lists just the roles the user holds.
function RoleChecklist({ options, heldCodes, selectedCodes, onChange, readOnly }) {
  const held = new Set(heldCodes);
  const selected = new Set(selectedCodes);

  // Read-only, the list is what the user holds — not the whole catalogue greyed out.
  const shown = readOnly ? options.filter((option) => held.has(option.code)) : options;

  const toggle = (code) => {
    onChange(selected.has(code) ? selectedCodes.filter((c) => c !== code) : [...selectedCodes, code]);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }} role='group' aria-label='Roles'>
      {shown.map((option) => {
        const checked = selected.has(option.code);
        const disabled = readOnly || Boolean(option.unavailableReason);
        const change = disabled ? null : checked && !held.has(option.code) ? 'added' : !checked && held.has(option.code) ? 'removed' : null;
        const rowTone = change ? tone[change] : null;
        const inputId = `edit-user-role-${option.code}`;
        return (
          <Box
            key={option.code}
            component='label'
            htmlFor={inputId}
            sx={{
              ...rowSx,
              cursor: disabled ? 'default' : 'pointer',
              ...(rowTone && { background: rowTone.bg, borderColor: 'transparent' }),
              '&:hover': disabled || rowTone ? {} : { borderColor: sa.color.teal },
            }}
          >
            <Checkbox
              id={inputId}
              checked={checked}
              disabled={disabled}
              onChange={() => toggle(option.code)}
              size='small'
              sx={{ p: 0, mt: '1px', color: sa.color.stroke, '&.Mui-checked': { color: sa.color.teal } }}
              inputProps={{ 'data-role-code': option.code }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: sa.font.family,
                    fontSize: 14,
                    fontWeight: sa.font.medium,
                    color: rowTone ? rowTone.fg : disabled && !checked ? sa.color.textGray : sa.color.text,
                  }}
                >
                  {option.name}
                </Typography>
                {change && <Box component='span' sx={badgeSx({ bg: sa.color.white, fg: rowTone.fg })}>{change === 'added' ? 'Added' : 'Removed'}</Box>}
              </Box>
              {option.description && <Typography sx={hintSx}>{option.description}</Typography>}
              {option.unavailableReason && !readOnly && (
                <Typography sx={{ ...hintSx, fontStyle: 'italic' }}>{option.unavailableReason}</Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

RoleChecklist.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      unavailableReason: PropTypes.string,
    })
  ).isRequired,
  heldCodes: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedCodes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

export default RoleChecklist;
